import React, { useId } from "react";

import NumberedList from "./NumberedList";
import { ACTION, LIST } from "./editorModel";
import { FIX_KINDS, MAX_FIX_CHARS, cleanDraft, requirementsOf } from "./editorRules";
import { TEXT } from "./editorText";

const T = TEXT.form;
const META_OF = Object.freeze({ [LIST.SYMPTOMS]: "lineMeta", [LIST.STEPS]: "stepMeta" });

/** Everything the lesson still needs, named exactly, or a line saying it is ready. */
function Requirements({ category, draft }) {
  const unmet = requirementsOf(category, cleanDraft(draft).draft).filter((r) => !r.ok);
  if (!unmet.length) return <p className="le-ready">{TEXT.ready}</p>;
  return (
    <div className="le-needed">
      <span className="le-label">{TEXT.stillNeeded}</span>
      <ul className="le-plain">{unmet.map((r) => <li key={r.key}>{TEXT.requirements[r.key]}</li>)}</ul>
    </div>
  );
}

/** What was done about the root cause: one of the three kinds, and how. */
function FixField({ id, draft, dispatch }) {
  return (
    <fieldset className="le-field le-fieldset">
      <legend className="le-label">{T.fix}</legend>
      <div className="le-control">
        <div className="le-choices">
          {FIX_KINDS.map((kind) => (
            <label key={kind} className="le-choice">
              <input type="radio" name={`${id}-fix-kind`} value={kind} checked={draft.fixKind === kind}
                onChange={() => dispatch({ type: ACTION.SET_FIX_KIND, value: kind })} />
              {TEXT.fixKinds[kind]}
            </label>
          ))}
        </div>
        <textarea id={`${id}-fix`} className="le-input" rows={5} maxLength={MAX_FIX_CHARS} value={draft.fixText}
          placeholder={T.fixHint} aria-label={T.fix}
          onChange={(e) => dispatch({ type: ACTION.SET_FIX_TEXT, value: e.target.value })} />
      </div>
    </fieldset>
  );
}

/** The one screen where the engineer teaches: category (shown), symptoms, troubleshooting steps, root cause, fix. */
export default function EditorForm({ state, dispatch }) {
  const id = useId();
  const { category, draft } = state;
  const list = (name, label, hint) => (
    <NumberedList id={`${id}-${name}`} label={label} hint={hint} items={draft[name]} meta={draft[META_OF[name]]}
      note={state.truncated && state.truncated[name] > 0 ? T.truncated(state.truncated[name]) : null}
      onChange={(index, value) => dispatch({ type: ACTION.SET_LIST_ITEM, list: name, index, value })}
      onAdd={() => dispatch({ type: ACTION.ADD_LIST_ITEM, list: name })}
      onRemove={(index) => dispatch({ type: ACTION.REMOVE_LIST_ITEM, list: name, index })} />
  );
  return (
    <section className="le-screen" aria-labelledby={`${id}-h`}>
      <div className="le-intro">
        <h3 className="le-h" id={`${id}-h`}>{T.heading}</h3>
        <p className="le-muted">{T.hint}</p>
      </div>

      <div className="le-fields">
        <div className="le-field">
          <span className="le-label">{T.category}</span>
          <p className="le-cause-name">{category ? category.label : T.noCategory}</p>
        </div>
        {list(LIST.SYMPTOMS, T.symptoms, T.symptomsHint)}
        {list(LIST.STEPS, T.steps, T.stepsHint)}
        <div className="le-field">
          <label className="le-label" htmlFor={`${id}-root`}>{T.rootCause}</label>
          <textarea id={`${id}-root`} className="le-input" rows={3} value={draft.rootCause} placeholder={T.rootCauseHint}
            onChange={(e) => dispatch({ type: ACTION.SET_ROOT_CAUSE, value: e.target.value })} />
        </div>
        <FixField id={id} draft={draft} dispatch={dispatch} />
      </div>

      <Requirements category={category} draft={draft} />
    </section>
  );
}
